import React from 'react';
import { StageSection, Prose, KeyPoints, NumberedFlow } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Accounts & Abstraction',
    tagline: 'Every account is a contract, so validation logic is programmable.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what native account abstraction actually means on Starknet
//   Q1 - the roles of __validate__ vs __execute__
export const blocks: StageBlock[] = [
    content('hero', () => (
        <StageSection gradientBorder eyebrow="Stage 3 of 8" title="There are no EOAs on Starknet">
            <Prose>
                Ethereum draws a hard line between externally owned accounts (EOAs), controlled purely by a private
                key with fixed validation rules, and smart contracts. Starknet erases that line: every account on
                Starknet, including the one a user directly controls, <strong>is a smart contract</strong>.
            </Prose>
            <Prose>
                This is called native <strong>account abstraction</strong>, "native" because it isn't bolted on
                top of the protocol the way ERC-4337 layers abstraction onto Ethereum. It's simply how every account
                works from the base layer up.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('validate-execute', () => (
        <StageSection title="__validate__ and __execute__">
            <Prose>
                An account contract exposes (at minimum) two entrypoints the protocol calls in sequence for every
                transaction it sends:
            </Prose>
            <NumberedFlow steps={[
                ['__validate__', 'Checks that the transaction is authorized: typically verifying a signature against the account\'s stored public key and checking the nonce. If this fails, the transaction is rejected before any state changes.'],
                ['__execute__', 'Runs the actual logic of the transaction: the calls the account wants to make. This is where state changes and side effects happen.'],
            ]} />
            <Prose>
                Because both are just contract code the account author writes, an account can implement any
                authorization scheme it wants: single-key signatures, multisig, social recovery, session keys, or
                custom fee-sponsorship logic, all without any protocol-level change.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('security-surface', () => (
        <StageSection title="Why this expands the attack surface">
            <Prose>
                On Ethereum, "who is allowed to spend from this address" is a fixed rule enforced by the protocol
                itself: whoever holds the private key. On Starknet, that rule is whatever code the account contract
                happens to contain. A bug in a wallet's account contract (a missing signature check, a replayable
                validation path, a way to call <code>__execute__</code> without passing <code>__validate__</code>)
                is a bug in the security of every asset that account holds.
            </Prose>
            <KeyPoints items={[
                <>Account abstraction moves authorization from protocol-enforced to contract-enforced: powerful, but only as safe as the account's own code.</>,
                <>The Cairo security challenges ahead include exactly this class of bug: signature and account-validation flaws that let a caller bypass the checks a wallet was supposed to enforce.</>,
            ]} />
        </StageSection>
    )),
];
